// noinspection SpellCheckingInspection
import WorkVersion from "work/work-agreements/versions/work-version";
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_TableInstance,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_Updater,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import WorkRecordCriteria from "work/work-records/work-record-criteria";
import WorkRecordCriteriaChips from "work/work-records/_views/work-record-criteria-chips";
import {
  getLastSuccessfulWorkAgreementQueryByInterface,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  isWorkAgreementLoadIssue,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementLoadIssue
} from "work/work-agreements/_store/work-agreement-redux-slice";
import {
  listsArchivedWork,
  readWorkAgreementChips,
  STAGE_PARAMETER,
  WORK_AGREEMENTS_CURRENT_VIEW,
  WORK_AGREEMENTS_PATH,
  workAgreementChipGroups,
  workVersionsPath,
  writeWorkAgreementChips,
} from "work/work-agreements/_views/work-agreement-list-chips";
import WorkAgreement from "work/work-agreements/work-agreement";
import { WorkAgreementOrderField } from "../_api/queries/_fields/work-agreement-order-field";
import {
  useGetWorkAgreementsQuery,
  useWorkAgreementListCounts,
} from "work/work-agreements/_api/work-agreement-api";
import { skipToken } from "@reduxjs/toolkit/query";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { DateCell } from "_shared/_views/tables/date-cells";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";
import { formatDate, formatDateRange, type DateValue } from "_shared/formatting/dates";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageToolbarButtons,
} from "_shared/_views/list-pages/list-page-heading";
import ListChips from "_shared/_views/list-pages/list-chips";
import type { ListChipCounts, ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";

// The name and the version beside it (#6636). The name keeps at least a few words; when the column
// cannot seat both, the version wraps under it rather than squeezing the name to an ellipsis.
const NameLine = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
  minWidth: 0,
  width: "100%",
  "& > :not(.MuiChip-root)": {
    flex: "1 1 12ch",
    minWidth: 0,
  },
  "& > .MuiChip-root": {
    flex: "0 0 auto",
  },
}));

const RowTitle = styled(Typography)(() => ({
  display: "block",
  fontSize: "1.1em",
  fontWeight: "600",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));

const columnHelper = createMRTColumnHelper<WorkAgreement | WorkAgreementLoadIssue>();

type SortingState = Array<{ id: string; desc: boolean }>;
type WorkAgreementTableSearchState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

/**
 * The field `GET work` orders by for each column the table sorts. Term sorts by its start, as the
 * hidden Start date column does; the first column for a field is the one a URL's sort marks.
 */
const orderFieldByColumnId: Readonly<Record<string, WorkAgreementOrderField>> = {
  name: WorkAgreementOrderField.Name,
  term: WorkAgreementOrderField.Start,
  start: WorkAgreementOrderField.Start,
  end: WorkAgreementOrderField.End,
  discount: WorkAgreementOrderField.Discount,
  archived: WorkAgreementOrderField.Archived,
};

/**
 * Where the columns go when the pane is short of room (#6711). Discount folds under the name first;
 * the hidden single-date columns fold under Term when the reader shows them.
 */
const workAgreementColumnFolds = {
  discount: { into: "name", priority: 1 },
  hireDate: { into: "term", priority: 2 },
  start: { into: "term", priority: 3 },
  end: { into: "term", priority: 4 },
  archived: { into: "term", priority: 5 },
} as const;

export default function WorkAgreementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  // The view the reader left the list in, kept on their account (#6714): a plain visit to `/work`
  // opens their starred view or that one, and a link is always shown as it says.
  const { remember } = useCurrentTableView(WORK_AGREEMENTS_CURRENT_VIEW);

  const isMdDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // #region Chips
  // Current work, or with the Archived chip the archive (#6716). Memoised on the URL because the list's
  // request is built from it in an effect, which must not run on every render.
  const chipsKey = searchParams.toString();
  const chips = useMemo(() => readWorkAgreementChips(new URLSearchParams(chipsKey)), [chipsKey]);
  const isArchiveView = listsArchivedWork(chips);
  const interfaceName = `workAgreementsPage:${isArchiveView ? "archived" : "active"}`;

  const workAgreementsQuery = useSelector(getWorkAgreementQueryByInterface(interfaceName));

  const paginatedWorkAgreementsJson = useSelector(getWorkAgreementsByQuery(workAgreementsQuery));
  const paginatedWorkAgreements = useMemo(() => {
    if (!paginatedWorkAgreementsJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const workAgreementJson of paginatedWorkAgreementsJson.data) {
      try {
        if (isWorkAgreementLoadIssue(workAgreementJson)) {
          deserializedData.push(workAgreementJson);
        } else {
          const workAgreement = WorkAgreement.fromJSON(workAgreementJson);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("WorkAgreement does not have an id", workAgreementJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading WorkAgreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in workAgreementJson ? (Guid.fromJSON(workAgreementJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading WorkAgreement." });
        console.error("Error deserializing WorkAgreement JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedWorkAgreementsJson]);

  const workAgreementsAPIQuery = useGetWorkAgreementsQuery(
    workAgreementsQuery ? { query: workAgreementsQuery, interfaceName } : skipToken
  );

  // The Archived chip's count, under the list's own criteria, from the list's one counts request
  // (#6709). It counts the archive whether or not the chip is on.
  const countsCriteria = useMemo(() => workAgreementCountsCriteria(new URLSearchParams(chipsKey)), [chipsKey]);
  const listCounts = useWorkAgreementListCounts({ criteria: countsCriteria });
  const archivedCount = listCounts.counts?.archived;
  const chipCounts = useMemo<ListChipCounts>(
    () => ({ [STAGE_PARAMETER]: { archived: archivedCount } }),
    [archivedCount],
  );

  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulWorkAgreementQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isWorkAgreementLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const workAgreement = WorkAgreement.fromJSON(item);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("Work Agreement does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Work Agreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Work Agreement." });
        console.error("Error deserializing Work Agreement JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson]);

  const tableWorkAgreements = paginatedWorkAgreements ?? lastSuccessfulQueryResponse;
  const tableWorkAgreementsHeaders = paginatedWorkAgreementsJson?.headers ?? lastSuccessfulQueryResponseJson?.headers;

  const globalFilter = useMemo(() => getGlobalFilterFromSearchParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const filterParameters = WorkAgreementFilterParameters.fromSearchParams({
      searchParams,
      overrides: {
        isArchived: isArchiveView
      }
    });
    const orderParameters = WorkAgreementOrderParameters.fromSearchParams(searchParams);

    const newQuery = WorkAgreementQuery.create({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromListPageSearchParams(searchParams),
    });
    if (workAgreementsQuery && newQuery.isEqualTo(workAgreementsQuery)) return;
    dispatch(
      updateWorkAgreementInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [isArchiveView, session.user?.legalEntityId.value, session, searchParams, workAgreementsQuery, dispatch, interfaceName]);

  /** Shows `next` and keeps it as the reader's view: every change the reader makes goes through here. */
  const showReaderChange = useCallback(
    (next: URLSearchParams) => {
      remember(`${WORK_AGREEMENTS_PATH}?${next.toString()}`);
      setSearchParams(next);
    },
    [remember, setSearchParams],
  );

  const updateSearchParamsFromTableState = useCallback(
    (nextState: WorkAgreementTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState, searchParams);
      if (newSearchParams.toString() === searchParams.toString()) return;
      showReaderChange(newSearchParams);
    },
    [searchParams, showReaderChange],
  );

  const handleChipsChange = useCallback(
    (selection: ListChipSelection) => showReaderChange(writeWorkAgreementChips(selection, searchParams)),
    [searchParams, showReaderChange],
  );

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      updateSearchParamsFromTableState({
        globalFilter: applyUpdater(updater, globalFilter),
        columnFilters,
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<Array<{ id: string; desc: boolean }>>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  useEffect(() => {
    if (workAgreementsAPIQuery.isError) {
      enqueueSnackbar("Error loading work agreement table", { variant: "error" });
    }
  }, [workAgreementsAPIQuery.isError]);

  // Name carries the description and Term carries the hire date (#6716), so the list reads in three
  // columns. Every column it had is still there, hidden, in Show/hide.
  const columns = useMemo(() => {
    const returnedColumns = [
      columnHelper.accessor(getWorkAgreementNameFilterValue, {
        header: "Name",
        id: "name",
        meta: { kind: "name" },
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ row, table }) => renderNameColumn(table, row.original, isArchiveView)
      }),
      columnHelper.accessor(getWorkAgreementStartDateSortValue, {
        header: "Term",
        id: "term",
        meta: { kind: "dateRange" },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: TermCell,
      }),
      columnHelper.accessor(getWorkAgreementDiscountSortValue, {
        header: "Discount",
        id: "discount",
        meta: {
          kind: "number",
          fold: { ...workAgreementColumnFolds.discount, render: foldedDiscount },
        },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: DiscountCell,
      }),
      columnHelper.accessor(getWorkAgreementDescriptionFilterValue, {
        header: "Description",
        id: "description",
        meta: { kind: "text" },
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ row, table }) => renderDescriptionColumn(table, row.original)
      }),
      // The server's search reads the name, the description and the fee schedule's categories, so
      // the columns below never mark a match (#6590).
      columnHelper.accessor(getWorkAgreementHireDateSortValue, {
        header: "Hired",
        id: "hireDate",
        meta: {
          kind: "date",
          fold: { ...workAgreementColumnFolds.hireDate, render: foldedDate("Hired", (row) => row.hireDate) },
        },
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: HireDateCell
      }),
      columnHelper.accessor(getWorkAgreementStartDateSortValue, {
        header: "Start date",
        id: "start",
        meta: {
          kind: "date",
          fold: { ...workAgreementColumnFolds.start, render: foldedDate("Starts", (row) => row.startDate) },
        },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: StartDateCell
      }),
      columnHelper.accessor(getWorkAgreementEndDateSortValue, {
        header: "End date",
        id: "end",
        meta: {
          kind: "date",
          fold: { ...workAgreementColumnFolds.end, render: foldedDate("Ends", (row) => row.endDate) },
        },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: EndDateCell
      }),
    ];
    if (isArchiveView) {
      returnedColumns.push(
        columnHelper.accessor(getWorkAgreementArchivedDateSortValue, {
          header: "Archived",
          id: "archived",
          meta: {
            kind: "date",
            fold: { ...workAgreementColumnFolds.archived, render: foldedDate("Archived", (row) => row.archiveDate) },
          },
          enableGlobalFilter: false,
          enableSorting: true,
          enableColumnFilter: false,
          Cell: ArchiveDateCell
        }),
      );
    }
    return returnedColumns;
  }, [isArchiveView]);

  const openWorkAgreement = useCallback(
    (workAgreement: WorkAgreement | WorkAgreementLoadIssue) => {
      if (isWorkAgreementLoadIssue(workAgreement)) {
        console.warn("Cannot open work agreement with loading issues.");
        return;
      }
      const destination = workAgreementDestination(workAgreement, isArchiveView);
      if (!destination) {
        console.error("Work Agreement ID is required");
        return;
      }
      navigate(destination);
    },
    [navigate, isArchiveView],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => (
      <ListPageHeading
        title="Work agreements"
        chips={
          <ListChips
            groups={workAgreementChipGroups}
            selection={chips}
            counts={chipCounts}
            onChange={handleChipsChange}
          />
        }
        mobileToolbar={
          isMdDisplaySize ? (
            <ListPageMobileToolbar>
              <ListPageMobileSearch table={table} />
              <TableToolsMenu table={table} />
            </ListPageMobileToolbar>
          ) : undefined
        }
        criteria={
          <WorkRecordCriteriaChips
            criteria={WorkRecordCriteria.WORK_AGREEMENTS}
            searchParams={searchParams}
            onChange={showReaderChange}
            shownElsewhere={WORK_AGREEMENT_CRITERIA_SHOWN_ELSEWHERE}
          />
        }
      />
    ),
    [chipCounts, chips, handleChipsChange, isMdDisplaySize, searchParams, showReaderChange],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => (
      <>
        {!isMdDisplaySize && (
          <ListPageToolbarButtons>
            <ToolbarButtons>
              <GlobalFilterTextField table={props.table} sx={searchFieldSpacing} />
              <TableToolsMenu table={props.table} />
            </ToolbarButtons>
          </ListPageToolbarButtons>
        )}
      </>
    ),
    [isMdDisplaySize],
  );

  const isPreparingWorkAgreementsQuery =
    !sessionPermissions || (Boolean(session.user?.legalEntityId) && !workAgreementsQuery);
  const isTableLoading = isPreparingWorkAgreementsQuery || workAgreementsAPIQuery.isFetching;
  const showTableSkeletons = !tableWorkAgreements && isTableLoading;
  const skeletonWorkAgreements = useMemo(
    () =>
      Array.from({ length: pagination.pageSize }, (_, index) => ({
        id: Guid.generate(),
        issue: `Loading work agreement ${index}`,
      })),
    [pagination.pageSize],
  );
  const tableData = useMemo(
    () => (showTableSkeletons ? skeletonWorkAgreements : (tableWorkAgreements ?? [])),
    [showTableSkeletons, skeletonWorkAgreements, tableWorkAgreements],
  );
  const tableRowCount = getTableRowCount({
    headers: tableWorkAgreementsHeaders,
    isShowingSkeletons: showTableSkeletons,
    pageSize: pagination.pageSize,
    tableWorkAgreementCount: tableWorkAgreements?.length,
  });

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<WorkAgreement | WorkAgreementLoadIssue> }) => ({
      onClick: showTableSkeletons ? undefined : () => openWorkAgreement(row.original),
      sx: {
        backgroundColor: !showTableSkeletons && isWorkAgreementLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: showTableSkeletons || isWorkAgreementLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openWorkAgreement, showTableSkeletons],
  );

  // The ⋯ that ends every row, with Open first (#6711). Revise, Leave Team and End Work Agreement all
  // live in the work agreement itself, which Open, the name and the row open.
  const renderRowActions = useCallback(
    ({ row }: { row: MRT_Row<WorkAgreement | WorkAgreementLoadIssue> }) => {
      if (showTableSkeletons || isWorkAgreementLoadIssue(row.original)) return null;
      const destination = workAgreementDestination(row.original, isArchiveView);
      if (!destination) return null;
      const name = row.original.name?.value;
      return <RowActions actions={[]} open={{ to: destination }} ariaLabel={name ? `Actions for ${name}` : undefined} />;
    },
    [isArchiveView, showTableSkeletons],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (workAgreementsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [workAgreementsAPIQuery.isError]);

  const tableOptions = useMemo<MRT_TableOptions<WorkAgreement | WorkAgreementLoadIssue>>(() => {
    return {
      columns,
      data: tableData,
      enableRowActions: true,
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderRowActions,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      enableColumnPinning: true,
      initialState: {
        columnOrder: [
          "name",
          "term",
          "discount",
          "description",
          "hireDate",
          "start",
          "end",
          "archived",
          "mrt-row-actions",
        ],
        columnPinning: { right: ["mrt-row-actions"] },
        columnVisibility: {
          description: false,
          hireDate: false,
          start: false,
          end: false,
        },
        showGlobalFilter: true,
      },
      manualFiltering: true,
      enableColumnFilters: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps: {
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative"
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden"
        },
      },
      muiTopToolbarProps: { sx: topToolbarFitsThePage },
      muiSearchTextFieldProps: ({ table }) =>
        searchTextFieldProps({
          searches: isArchiveView ? "archived work agreements" : "work agreements",
          table,
        }),
      muiToolbarAlertBannerProps,
      rowCount: tableRowCount,
      pageCount: Math.ceil(tableRowCount / pagination.pageSize),
      enablePagination: true,
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: workAgreementsAPIQuery.isError,
        isLoading: isTableLoading,
        onRetry: () => void workAgreementsAPIQuery.refetch(),
        subject: isArchiveView ? "archived work agreements" : "work agreements",
      }),

      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      positionGlobalFilter: "none",
      state: {
        columnFilters,
        globalFilter,
        isLoading: isTableLoading,
        pagination,
        showAlertBanner: Boolean(workAgreementsAPIQuery.isError),
        showGlobalFilter: true,
        showProgressBars: workAgreementsAPIQuery.isFetching,
        sorting,
        showSkeletons: showTableSkeletons,
      },
    };
  }, [
    columns,
    tableData,
    tableRowCount,
    muiTableBodyRowProps,
    renderRowActions,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    muiToolbarAlertBannerProps,
    pagination,
    columnFilters,
    isArchiveView,
    globalFilter,
    isTableLoading,
    workAgreementsAPIQuery,
    showTableSkeletons,
    sorting,
    setColumnFilters,
    setGlobalFilter,
    setPagination,
    setSorting,
  ]);

  if (!sessionPermissions) return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} persistColumnVisibilityAs="work-agreements" cards />;
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} persistColumnVisibilityAs="work-agreements" cards />;
}

/**
 * What the counts request is narrowed by: the list's criteria, less the archived state the counts
 * split on, and less the page.
 */
export function workAgreementCountsCriteria(searchParams: URLSearchParams): string {
  const criteria = WorkAgreementFilterParameters.fromSearchParams({ searchParams }).searchParams;
  criteria.delete("isArchived");
  return criteria.toString();
}

function getGlobalFilterFromSearchParams(searchParams: URLSearchParams): string {
  return searchParams.get("query") ?? "";
}

function getColumnFiltersFromSearchParams(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  const name = searchParams.get("name");
  const description = searchParams.get("description");

  if (name) filters.push({ id: "name", value: name });
  if (description) filters.push({ id: "description", value: description });

  return filters;
}

function getSortingFromSearchParams(searchParams: URLSearchParams): SortingState {
  const orderByParam = searchParams.get("orderBy")?.toLowerCase();
  const columnId = Object.keys(orderFieldByColumnId).find(
    (id) => orderFieldByColumnId[id].toLowerCase() === orderByParam,
  );
  if (!columnId) return [];

  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir = Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam);

  return [{
    id: columnId,
    desc: orderDir !== OrderDirection.Ascending,
  }];
}

function getPaginationFromSearchParams(searchParams: URLSearchParams): PaginationState {
  const { pageIndex, pageSize } = PaginationParameters.fromListPageSearchParams(searchParams);
  return { pageIndex, pageSize };
}

function getSearchParamsFromTableState(
  state: WorkAgreementTableSearchState,
  currentSearchParams: URLSearchParams,
): URLSearchParams {
  const { globalFilter, columnFilters, sorting, pagination } = state;
  const newSearchParams = new URLSearchParams();
  // The chips are the page's, not the table's: a sort or a search keeps them as they are.
  for (const stage of currentSearchParams.getAll(STAGE_PARAMETER)) newSearchParams.append(STAGE_PARAMETER, stage);
  if (globalFilter) {
    newSearchParams.set("query", globalFilter);
  }

  const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
  if (nameFilter) {
    newSearchParams.set("name", nameFilter);
  }

  const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
  if (descriptionFilter) {
    newSearchParams.set("description", descriptionFilter);
  }

  const orderBy = sorting.map((sort) => orderFieldByColumnId[sort.id]).find(Boolean);
  const orderingField = sorting.find((sort) => orderFieldByColumnId[sort.id]);
  if (orderBy && orderingField) {
    newSearchParams.set("orderBy", orderBy);
    newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  const paginationPageIndex = pagination.pageIndex ?? 0;
  newSearchParams.set("pageIndex", paginationPageIndex.toString());
  const paginationPageSize = pagination.pageSize ?? LIST_PAGE_SIZE;
  newSearchParams.set("pageSize", paginationPageSize.toString());

  return WorkRecordCriteria.WORK_AGREEMENTS.carryOver(currentSearchParams, newSearchParams, WORK_AGREEMENT_TABLE_PARAMETERS);
}

/**
 * The criteria the table's own controls write: the search box and the column filters. The table
 * keeps or clears these itself; every other criterion in the URL came from a link and is kept.
 */
const WORK_AGREEMENT_TABLE_PARAMETERS: readonly string[] = ["query", "name", "description"];

/**
 * The search box shows `query` and the Archived chip `stage`, so neither gets a criteria chip. The
 * versions a badge or a link asked for do, which is how the reader goes back to the latest ones.
 */
const WORK_AGREEMENT_CRITERIA_SHOWN_ELSEWHERE: readonly string[] = ["query", STAGE_PARAMETER];

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

function getTableRowCount({
  headers,
  isShowingSkeletons,
  pageSize,
  tableWorkAgreementCount,
}: {
  headers?: PaginationHeaders;
  isShowingSkeletons: boolean;
  pageSize: number;
  tableWorkAgreementCount?: number;
}): number {
  if (isShowingSkeletons) return pageSize;
  return headers?.totalElements ?? tableWorkAgreementCount ?? 0;
}

function LoadIssueCell({ workAgreement }: Readonly<{ workAgreement: WorkAgreementLoadIssue }>) {
  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "4000px",
            whiteSpace: "normal",
          },
        },
      }}
      title={`Our staff has been notified. Reference id: ${workAgreement.id.value}`}
    >
      <Box component="span" sx={{ color: "error.main" }}>
        Error Loading Work Agreement
      </Box>
    </Tooltip>
  );
}

/**
 * Where a row goes, in one place, because the row, the link in its name cell and its Open item have
 * to agree. The archive's agreements open under the archived route, current work under the active one.
 */
function workAgreementDestination(
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
  isArchiveView: boolean,
): string | undefined {
  if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.id) return undefined;
  return `${WORK_AGREEMENTS_PATH}/${isArchiveView ? "archived" : "active"}/view/${workAgreement.id}`;
}

function renderNameColumn(
  table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>,
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
  isArchiveView: boolean,
) {
  if (table.getState().isLoading) return;
  if (isWorkAgreementLoadIssue(workAgreement)) return <LoadIssueCell workAgreement={workAgreement} />;
  if (!workAgreement.name) return <NoValueDash />;
  return (
    <CondensedCell
      title={
        <NameLine>
          <RowTitle>
            <RowLink to={workAgreementDestination(workAgreement, isArchiveView)}>
              <SearchMatch table={table} text={workAgreement.name.value} />
            </RowLink>
          </RowTitle>
          {/* The badge lists the work's earlier versions, in place of a chip for every work (#7090). */}
          <WorkVersionChip version={WorkVersion.of(workAgreement)} to={workVersionsPath(workAgreement)} sx={{ marginLeft: 0 }} />
        </NameLine>
      }
      // The server's search reads the description as well as the name (#6590).
      secondary={workAgreement.description ? <SearchMatch table={table} text={workAgreement.description.value} /> : undefined}
      after={
        // Work the reader left is in their archive as it was when they left (#5833).
        workAgreement.frozenAt && (
          <Typography variant="caption" color="text.secondary" data-testid="left-work-caption">
            You left this work on {formatDate(workAgreement.frozenAt)}
          </Typography>
        )
      }
    />
  );
}

function renderDescriptionColumn(
  table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>,
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
) {
  if (table.getState().isLoading) return;
  if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.description) return <NoValueDash />;
  return (
    <TruncatedCell>
      <Typography variant="body2">
        <SearchMatch table={table} text={workAgreement.description.value} />
      </Typography>
    </TruncatedCell>
  );
}

function getWorkAgreementNameFilterValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): string {
  if (isWorkAgreementLoadIssue(workAgreement)) return "";
  return workAgreement.name?.value ?? "";
}

function getWorkAgreementDescriptionFilterValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): string {
  if (isWorkAgreementLoadIssue(workAgreement)) return "";
  return workAgreement.description?.value ?? "";
}

function getWorkAgreementHireDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.hireDate?.value ?? 0;
}

function getWorkAgreementStartDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.startDate?.value ?? 0;
}

function getWorkAgreementEndDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.endDate?.value ?? 0;
}

function getWorkAgreementArchivedDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.archiveDate?.value ?? 0;
}

function getWorkAgreementDiscountSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return Masked.isPresentNotMasked(workAgreement.discount) ? workAgreement.discount.value : 0;
}

/**
 * The work's term, over when it was hired (#6716): "Mar 3 – Dec 9, 2027", then "Hired Feb 20, 2027".
 * Work with no end yet reads "From Mar 3, 2027".
 */
export function termText(workAgreement: WorkAgreement): string | undefined {
  const range = formatDateRange(workAgreement.startDate, workAgreement.endDate);
  if (range) return range;
  const start = formatDate(workAgreement.startDate);
  if (start) return `From ${start}`;
  const end = formatDate(workAgreement.endDate);
  return end ? `Until ${end}` : undefined;
}

function TermCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  if (isWorkAgreementLoadIssue(row.original)) return <NoValueDash />;
  const hired = formatDate(row.original.hireDate);
  return (
    <CondensedCell
      title={termText(row.original) ?? <NoValueDash />}
      secondary={hired ? `Hired ${hired}` : undefined}
    />
  );
}

/** A date column's value as it reads folded under Term: "Hired Mar 3, 2027". */
function foldedDate(
  label: string,
  dateOf: (workAgreement: WorkAgreement) => DateValue,
): (row: WorkAgreement | WorkAgreementLoadIssue) => string | undefined {
  return (row) => {
    if (!(row instanceof WorkAgreement)) return undefined;
    const date = formatDate(dateOf(row));
    return date ? `${label} ${date}` : undefined;
  };
}

/** The discount as it reads folded under the name: "Discount 10". */
function foldedDiscount(row: WorkAgreement | WorkAgreementLoadIssue): string | undefined {
  if (!(row instanceof WorkAgreement)) return undefined;
  return Masked.isPresentNotMasked(row.discount) ? `Discount ${row.discount.toString()}` : undefined;
}

function StartDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.startDate;
  return <DateCell value={value || undefined} />;
}

function EndDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.endDate;
  return <DateCell value={value || undefined} />;
}

function HireDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.hireDate;
  return <DateCell value={value || undefined} />;
}

function ArchiveDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.archiveDate;
  return <DateCell value={value || undefined} />;
}

function DiscountCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.discount;
  return Masked.isPresentNotMasked(value) ? value.toString() : <NoValueDash />;
}
